import { Package, Plus, Search, Trash2, Pencil, Image as ImageIcon, MapPin, Users, Download, CheckCircle2, Circle, History, FileDown, X, CalendarDays, FileX } from 'lucide-react';
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import AddShipmentModal from '../components/AddShipmentModal';
import SellerManagerModal from '../components/SellerManagerModal';
import ImageModal from '../components/ImageModal';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import { normalizeRole } from '../utils/roleUtils';
import { formatRecordDate, formatRecordTime } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const toLocalISODate = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Haftanin baslangici Pazartesi kabul edilir
const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    now.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    return now;
};

const QUICK_RANGE_OPTIONS = [
    { value: 'today', label: 'Bugün' },
    { value: 'week', label: 'Bu Hafta' },
    { value: 'all', label: 'Tümü' },
];

const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'Tüm Durumlar' },
    { value: 'pending_invoice', label: 'Fatura Bekleyen' },
    { value: 'ready', label: 'Sevke Hazır' },
    { value: 'shipped', label: 'Sevk Edilen' },
];

const AUDIT_ACTION_LABELS = {
    invoiced: 'Fatura kesildi',
    uninvoiced: 'Fatura işareti geri alındı',
    invoiceless: 'Faturasız gidecek işaretlendi',
    uninvoiceless: 'Faturasız işareti geri alındı',
    shipped: 'Sevk edildi',
    unshipped: 'Sevk işareti geri alındı',
    proof_uploaded: 'Sevk kanıtı yüklendi',
    proof_deleted: 'Sevk kanıtı silindi',
};

const AUTO_REFRESH_INTERVAL_MS = 30000;

export const ShipmentLifecycleBadge = ({ row }) => {
    if (row.isShipped === 1) {
        return (
            <div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                    <CheckCircle2 size={12} />
                    Sevk edildi
                </span>
                {row.shippedBy && (
                    <div className="mt-1 text-[11px] text-dark-muted">{row.shippedBy} · {formatRecordDate(row.shippedAt)}</div>
                )}
            </div>
        );
    }
    if (row.isInvoiced === 1) {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                Sevke hazır
            </span>
        );
    }
    if (row.isInvoiceless === 1) {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-500/10 text-violet-400 border border-violet-500/20">
                Sevke hazır · Faturasız
            </span>
        );
    }
    // Dikkat cekmesi icin yanip soner: fatura karari verilmemis kayit aksiyon bekliyor demektir
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/40 animate-pulse">
            <Circle size={8} className="fill-red-400" />
            Fatura bekliyor
        </span>
    );
};

const Shipments = () => {
    const { user } = useContext(AuthContext);
    const normalizedRole = normalizeRole(user?.role);
    const [shipments, setShipments] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSellerModalOpen, setIsSellerModalOpen] = useState(false);
    const [shipmentToEdit, setShipmentToEdit] = useState(null);
    const [viewImage, setViewImage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [quickRange, setQuickRange] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [auditShipment, setAuditShipment] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);

    // Onay süreci ayarı (sadece admin görür/değiştirir). null = henüz yüklenmedi / yüklenemedi
    const [approvalSetting, setApprovalSetting] = useState(null);
    const [isSavingApproval, setIsSavingApproval] = useState(false);

    // Sayfalama (Pagination)
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
    const scrollSentinelRef = useRef(null);

    const fetchShipments = useCallback(async (pageNum = 1, append = false, { silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const res = await api.get('/shipments', {
                params: {
                    search: searchTerm,
                    page: pageNum,
                    limit: 20,
                    status: statusFilter || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                }
            });
            if (res.data.data) {
                setShipments(prev => append ? [...prev, ...res.data.data] : res.data.data);
                setPagination(res.data.pagination);
            } else {
                setShipments(res.data);
            }
        } catch (error) {
            console.error('Kargolar alınamadı', error);
            toast.error('Kargo listesi yüklenemedi.');
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [searchTerm, statusFilter, startDate, endDate]);

    const fetchSellers = async () => {
        try {
            const res = await api.get('/sellers');
            setSellers(res.data);
        } catch (error) {
            console.error('Satıcılar alınamadı', error);
        }
    };

    // Debounce search ve fetch tetikleyici
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchShipments(1, false);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchShipments]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            fetchShipments(1, false, { silent: true });
        }, AUTO_REFRESH_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [fetchShipments]);

    useEffect(() => {
        if (normalizedRole === 'admin' || normalizedRole === 'staff' || normalizedRole === 'shipment_staff') {
            fetchSellers();
        }
    }, [normalizedRole]);

    // Infinite Scroll IntersectionObserver
    const loadMore = useCallback(() => {
        if (!isLoading && pagination.page < pagination.totalPages) {
            fetchShipments(pagination.page + 1, true);
        }
    }, [fetchShipments, isLoading, pagination.page, pagination.totalPages]);

    useEffect(() => {
        const sentinel = scrollSentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMore]);

    // Hizli tarih filtresi (Bugün / Bu Hafta / Tümü)
    const applyQuickRange = (value) => {
        setQuickRange(value);
        if (value === 'all') {
            setStartDate('');
            setEndDate('');
        } else if (value === 'today') {
            const today = toLocalISODate(new Date());
            setStartDate(today);
            setEndDate(today);
        } else if (value === 'week') {
            setStartDate(toLocalISODate(getWeekStart()));
            setEndDate(toLocalISODate(new Date()));
        }
    };

    // Elle tarih secimi hizli butonlardan bagimsiz "ozel aralik" sayilir
    const handleDateInput = (field, value) => {
        setQuickRange('custom');
        if (field === 'start') setStartDate(value);
        else setEndDate(value);
    };

    // Fatura Kesildi / Faturasız Gidecek toggle (sadece admin; ikisi birbirini dışlar, server da uygular)
    const handleToggleInvoiceField = async (row, field) => {
        if (normalizedRole !== 'admin') {
            toast.error('Bu işlem için yetkiniz bulunmuyor.');
            return;
        }
        const newValue = row[field] === 1 ? 0 : 1;
        const successMessage = field === 'isInvoiced'
            ? (newValue === 1 ? 'Fatura kesildi olarak işaretlendi.' : 'Fatura işareti geri alındı.')
            : (newValue === 1 ? 'Faturasız gidecek olarak işaretlendi.' : 'Faturasız işareti geri alındı.');
        try {
            const res = await api.patch(`/shipments/${row.id}/status`, { [field]: newValue });
            toast.success(successMessage);
            // Sayfa zıplamasın diye listeyi baştan çekmek yerine local state güncellenir
            setShipments(prev => prev.map(s => s.id === row.id ? { ...s, ...res.data } : s));
        } catch (error) {
            const message = error.response?.data?.message;
            if (error.response?.status === 403) toast.error(message || 'Bu işlem için yetkiniz bulunmuyor.');
            else if (error.response?.status === 409) { toast.error(message || 'Kayıt değişti, liste yenileniyor.'); fetchShipments(1, false); }
            else toast.error('Durum güncellenemedi!');
        }
    };

    const handleShowAudit = async (row) => {
        setAuditShipment(row);
        setAuditLoading(true);
        try {
            const res = await api.get(`/shipments/${row.id}/audit-logs`);
            setAuditLogs(res.data || []);
        } catch (error) {
            toast.error('İşlem geçmişi yüklenemedi.');
            setAuditLogs([]);
        } finally {
            setAuditLoading(false);
        }
    };

    const handleDownloadProof = (id) => {
        window.open(`${api.defaults.baseURL}/shipments/${id}/proof/download`, '_blank');
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu sevkiyat kaydını silmek istediğinize emin misiniz?')) {
            try {
                await api.delete(`/shipments/${id}`);
                toast.success('Kargo kaydı silindi.');
                fetchShipments();
            } catch (error) {
                toast.error('Silme başarısız. Yetkinizi kontrol edin.');
            }
        }
    };

    // Sevkiyat Excel Export
    const handleExportShipments = () => {
        if (!shipments || shipments.length === 0) {
            toast.error('Dışa aktarılacak kargo verisi yok.');
            return;
        }
        const exportData = shipments.map(row => ({
            'Tarih': formatRecordDate(row.createdAt),
            'Satıcı': row.seller,
            'Alıcı': row.buyer,
            'Gönderim Yolu': row.route,
            'Ödeme': row.payer,
            'Şehir': row.city,
            'Çuval Sayısı': row.sackCount,
            'Ekleyen': row.createdBy || 'Bilinmiyor',
            'Durum': row.isShipped === 1 ? 'Sevk edildi' : (row.isInvoiced === 1 ? 'Sevke hazır' : (row.isInvoiceless === 1 ? 'Sevke hazır (Faturasız)' : 'Fatura bekliyor')),
            'Sevk Eden': row.shippedBy || '',
            'Sevk Tarihi': row.shippedAt ? formatRecordDate(row.shippedAt) : ''
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sevkiyat");
        XLSX.writeFile(workbook, "Sevkiyat_Raporu.xlsx");
        toast.success('Sevkiyat raporu indirildi.');
    };

    useEffect(() => {
        if (normalizedRole !== 'admin') return;

        let cancelled = false;
        api.get('/shipments/settings')
            .then((res) => { if (!cancelled) setApprovalSetting(res.data); })
            .catch(() => { /* ayar okunamazsa anahtar gizli kalır, sayfa etkilenmez */ });

        return () => { cancelled = true; };
    }, [normalizedRole]);

    const handleToggleApproval = async () => {
        if (!approvalSetting || isSavingApproval) return;

        const next = !approvalSetting.approvalRequired;
        const message = next
            ? 'Onay süreci AÇILSIN mı?\n\nBundan sonra eklenen sevkiyatlar siz "Fatura Kesildi" ya da "Faturasız Gidecek" işaretleyene kadar "Fatura bekliyor" durumunda kalır ve sevk edilemez.\n\nMevcut kayıtlar etkilenmez.'
            : 'Onay süreci KAPATILSIN mı?\n\nBundan sonra eklenen sevkiyatlar eklendiği anda otomatik onaylanır ("Faturasız Gidecek", işaretleyen: Sistem) ve sevke hazır olur.\n\nMevcut "fatura bekliyor" kayıtlar etkilenmez.';

        if (!window.confirm(message)) return;

        setIsSavingApproval(true);
        try {
            const res = await api.put('/shipments/settings', { approvalRequired: next });
            setApprovalSetting(res.data);
            toast.success(next ? 'Onay süreci açıldı. Yeni sevkiyatlar onay bekleyecek.' : 'Onay süreci kapatıldı. Yeni sevkiyatlar otomatik onaylanacak.');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Onay süreci ayarı kaydedilemedi.');
        } finally {
            setIsSavingApproval(false);
        }
    };

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text mb-2 tracking-tight">Sevkiyat</h1>
                    <p className="text-dark-muted text-sm md:text-base">Tüm gönderilen kargoların ve faturaların listelendiği yönetim ekranı.</p>
                </div>

                <div className="flex flex-wrap gap-3 w-full md:w-auto">
                    {/* EXCEL EXPORT */}
                    <button
                        onClick={handleExportShipments}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-600/20 hover:bg-green-600 border border-green-500/50 text-green-400 hover:text-white px-5 py-2.5 rounded-xl font-bold tracking-wide transition-all shadow-lg hover:scale-105 active:scale-95"
                    >
                        <Download size={18} />
                        <span className="text-sm">EXCEL İNDİR</span>
                    </button>

                    {/* SATICI YÖNETİMİ (SADECE ADMİN) */}
                    {normalizedRole === 'admin' && (
                        <button
                            onClick={() => setIsSellerModalOpen(true)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 px-5 py-3 rounded-xl font-bold tracking-wide transition-all shadow-lg hover:scale-105 active:scale-95"
                        >
                            <Users size={20} className="stroke-[2.5] text-primary-400" />
                            <span>SATICI YÖNETİMİ</span>
                        </button>
                    )}

                    {/* YENİ KARGO BUTONU */}
                    {(normalizedRole === 'admin' || normalizedRole === 'staff' || normalizedRole === 'shipment_staff') && (
                        <button
                            onClick={() => { setShipmentToEdit(null); setIsAddModalOpen(true); }}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-5 py-3 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-primary-500/20 hover:scale-105 active:scale-95"
                        >
                            <Plus size={20} className="stroke-[3]" />
                            <span>YENİ KARGO EKLE</span>
                        </button>
                    )}
                </div>
            </div>

            {/* ONAY SÜRECİ ANAHTARI (SADECE ADMİN) */}
            {normalizedRole === 'admin' && approvalSetting && (
                <div className="mb-6 flex items-start gap-4 rounded-xl border border-dark-border bg-dark-card p-4">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={approvalSetting.approvalRequired}
                        aria-label="Onay süreci"
                        onClick={handleToggleApproval}
                        disabled={isSavingApproval}
                        className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border transition-colors before:absolute before:-inset-3 disabled:cursor-wait disabled:opacity-60 ${
                            approvalSetting.approvalRequired ? 'border-primary-500 bg-primary-600' : 'border-dark-border bg-dark-muted/40'
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                approvalSetting.approvalRequired ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </button>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-dark-text">
                            Onay süreci:{' '}
                            <span className={`ml-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${approvalSetting.approvalRequired ? 'tone-warning' : 'tone-info'}`}>
                                {approvalSetting.approvalRequired ? 'Açık' : 'Kapalı (otomatik onay)'}
                            </span>
                        </p>
                        <p className="mt-1 text-xs text-dark-muted">
                            {approvalSetting.approvalRequired
                                ? 'Yeni sevkiyatlar siz "Fatura Kesildi" ya da "Faturasız Gidecek" işaretleyene kadar "Fatura bekliyor" durumunda kalır ve sevk edilemez.'
                                : 'Yeni sevkiyatlar eklendiği anda otomatik onaylanır ("Faturasız Gidecek", işaretleyen: Sistem) ve sevke hazır olur. Onay vermeye başlarken anahtarı açın.'}
                        </p>
                        {approvalSetting.updatedBy && (
                            <p className="mt-1 text-[11px] text-dark-muted">
                                Son değişiklik: {approvalSetting.updatedBy} • {formatRecordDate(approvalSetting.updatedAt)} {formatRecordTime(approvalSetting.updatedAt)}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* ARAMA + DURUM + TARİH FİLTRELERİ */}
            <div className="glass p-4 rounded-xl mb-6 flex flex-col gap-4 border border-slate-700/50">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                            <Search size={18} />
                        </div>
                        <input
                            type="text"
                            placeholder="Satıcı, Alıcı, Şehir veya Gönderim Yolu Ara..."
                            className="w-full pl-11 pr-4 py-3 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full md:w-56 px-4 py-3 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                    >
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* TARİH: hızlı butonlar + özel aralık */}
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex rounded-xl overflow-hidden border border-slate-700 w-full md:w-auto">
                        {QUICK_RANGE_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => applyQuickRange(opt.value)}
                                className={`flex-1 md:flex-none px-5 py-2.5 text-sm font-bold tracking-wide transition-colors ${quickRange === opt.value
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700'}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <CalendarDays size={18} className="text-slate-400 shrink-0" />
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => handleDateInput('start', e.target.value)}
                            className="flex-1 md:w-40 px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all [color-scheme:dark]"
                            title="Başlangıç tarihi"
                        />
                        <span className="text-slate-500 text-sm">—</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => handleDateInput('end', e.target.value)}
                            className="flex-1 md:w-40 px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all [color-scheme:dark]"
                            title="Bitiş tarihi"
                        />
                        {quickRange === 'custom' && (startDate || endDate) && (
                            <button
                                onClick={() => applyQuickRange('all')}
                                className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors shrink-0"
                                title="Tarih filtresini temizle"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* KARGO LİSTESİ */}
            <div className="glass rounded-xl border border-dark-border overflow-hidden shadow-2xl relative z-0">
                <div className="mobile-table-hint lg:hidden">Tüm sevkiyat bilgileri ve işlemler için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto min-h-[400px]">
                    <table className="mobile-complete-table w-full text-left border-collapse text-sm whitespace-nowrap">
                        <thead>
                            <tr className="bg-dark-card text-dark-muted border-b border-dark-border">
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs">Satıcı</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden sm:table-cell">Alıcı</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden md:table-cell">Gönderim</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden lg:table-cell">Ödeme</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden md:table-cell">Şehir</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs">Çuval</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden lg:table-cell">Ekleyen</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs hidden md:table-cell">Tarih</th>
                                <th className="px-3 md:px-6 py-4 font-semibold uppercase tracking-wider text-xs">Durum</th>
                                <th className="px-3 md:px-6 py-4 font-semibold text-right uppercase tracking-wider text-xs">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border text-dark-text">
                            {shipments.length > 0 ? (
                                shipments.map((row) => (
                                    <tr key={row.id} className="hover:bg-dark-hover transition-colors group">
                                        <td className="px-3 md:px-6 py-4 font-medium text-dark-text">
                                            <div className="text-xs md:text-sm">{row.seller}</div>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 text-dark-text/80 hidden sm:table-cell">
                                            <div className="text-xs md:text-sm">{row.buyer}</div>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 hidden md:table-cell">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                <Package size={14} />
                                                {row.route}
                                            </span>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 text-dark-muted font-medium hidden lg:table-cell">{row.payer}</td>
                                        <td className="px-3 md:px-6 py-4 hidden md:table-cell">
                                            <div className="flex items-center gap-1.5 text-dark-muted">
                                                <MapPin size={16} className="text-dark-muted" />
                                                {row.city}
                                            </div>
                                        </td>
                                        <td className="px-3 md:px-6 py-4">
                                            <span className="font-bold text-dark-text">{row.sackCount}</span> <span className="text-dark-muted text-xs md:text-sm">Adet</span>
                                        </td>
                                        <td className="px-3 md:px-6 py-4 text-xs text-dark-muted hidden lg:table-cell">{row.createdBy || 'Bilinmiyor'}</td>
                                        <td className="px-3 md:px-6 py-4 text-xs text-dark-muted hidden md:table-cell">
                                            <div>{formatRecordDate(row.createdAt)}</div>
                                            <div className="mt-1 text-[11px] text-dark-muted/70">{formatRecordTime(row.createdAt)}</div>
                                        </td>
                                        <td className="px-3 md:px-6 py-4">
                                            <ShipmentLifecycleBadge row={row} />
                                        </td>

                                        <td className="px-3 md:px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1 md:gap-2">
                                                {normalizedRole === 'admin' && (
                                                    <button
                                                        onClick={() => handleToggleInvoiceField(row, 'isInvoiced')}
                                                        disabled={row.isShipped === 1}
                                                        className={`p-1 md:p-2 rounded-lg transition-colors ${row.isShipped === 1
                                                            ? 'text-dark-muted/40 cursor-not-allowed'
                                                            : row.isInvoiced === 1
                                                                ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
                                                                : 'text-dark-muted hover:text-amber-400 hover:bg-amber-500/10'}`}
                                                        title={row.isShipped === 1 ? 'Sevk edilmiş kaydın fatura durumu değiştirilemez' : (row.isInvoiced === 1 ? 'Fatura işaretini geri al' : 'Faturası kesildi olarak işaretle')}
                                                    >
                                                        {row.isInvoiced === 1
                                                            ? <CheckCircle2 size={16} className="md:w-5 md:h-5" />
                                                            : <Circle size={16} className="md:w-5 md:h-5" />}
                                                    </button>
                                                )}
                                                {normalizedRole === 'admin' && (
                                                    <button
                                                        onClick={() => handleToggleInvoiceField(row, 'isInvoiceless')}
                                                        disabled={row.isShipped === 1}
                                                        className={`p-1 md:p-2 rounded-lg transition-colors ${row.isShipped === 1
                                                            ? 'text-dark-muted/40 cursor-not-allowed'
                                                            : row.isInvoiceless === 1
                                                                ? 'text-violet-400 hover:text-violet-300 hover:bg-violet-500/10'
                                                                : 'text-dark-muted hover:text-violet-400 hover:bg-violet-500/10'}`}
                                                        title={row.isShipped === 1 ? 'Sevk edilmiş kaydın fatura durumu değiştirilemez' : (row.isInvoiceless === 1 ? 'Faturasız işaretini geri al' : 'Faturasız gidecek olarak işaretle')}
                                                    >
                                                        <FileX size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {row.shipProofPath && (
                                                    <button
                                                        onClick={() => handleDownloadProof(row.id)}
                                                        className="p-1 md:p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors"
                                                        title="Sevk kanıtını indir"
                                                    >
                                                        <FileDown size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {(normalizedRole === 'admin' || normalizedRole === 'boss') && (
                                                    <button
                                                        onClick={() => handleShowAudit(row)}
                                                        className="p-1 md:p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-lg transition-colors"
                                                        title="İşlem geçmişi"
                                                    >
                                                        <History size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {row.imageUrl && (
                                                    <button
                                                        onClick={() => setViewImage(process.env.NODE_ENV === 'production' ? row.imageUrl : `http://localhost:5000${row.imageUrl}`)}
                                                        className="p-1 md:p-2 text-primary-400 hover:text-primary-500 hover:bg-primary-500/20 rounded-lg transition-colors"
                                                        title="Çuval/Fiş Resmi Gör"
                                                    >
                                                        <ImageIcon size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {normalizedRole === 'admin' && (
                                                    <button onClick={() => { setShipmentToEdit(row); setIsAddModalOpen(true); }} className="p-1 md:p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-lg transition-colors" title="Düzenle">
                                                        <Pencil size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {normalizedRole === 'admin' && (
                                                    <button onClick={() => handleDelete(row.id)} className="p-1 md:p-2 text-dark-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Sil">
                                                        <Trash2 size={16} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="px-6 py-12 text-center text-dark-muted">
                                        <div className="flex flex-col items-center justify-center">
                                            <Package size={48} className="mb-4 text-dark-muted opacity-50" />
                                            <p className="text-lg font-medium">Kargo kaydı bulunamadı.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Sentinel */}
            {!isLoading && pagination.page < pagination.totalPages && (
                <div ref={scrollSentinelRef} className="py-8 flex justify-center">
                    <div className="w-8 h-8 rounded-full border-t-2 border-r-2 border-primary-500 animate-spin"></div>
                </div>
            )}

            {/* End of list message */}
            {!isLoading && pagination.page >= pagination.totalPages && shipments.length > 0 && (
                <div className="py-8 text-center text-dark-muted font-medium text-sm">
                    Tüm kayıtlar yüklendi.
                </div>
            )}

            <AddShipmentModal
                isOpen={isAddModalOpen}
                editData={shipmentToEdit}
                sellers={sellers}
                onClose={() => { setIsAddModalOpen(false); setShipmentToEdit(null); }}
                onRefresh={({ resetToFirstPage } = {}) => fetchShipments(resetToFirstPage ? 1 : pagination.page, false)}
            />

            <SellerManagerModal
                isOpen={isSellerModalOpen}
                onClose={() => setIsSellerModalOpen(false)}
                onRefreshSellers={fetchSellers}
            />

            {/* FOTOĞRAF GÖRÜNTÜLEME MODALI */}
            <ImageModal
                isOpen={!!viewImage}
                imageUrl={viewImage}
                onClose={() => setViewImage(null)}
            />

            {/* İŞLEM GEÇMİŞİ MODALI */}
            {auditShipment && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setAuditShipment(null)}>
                    <div className="glass w-full max-w-lg rounded-2xl border border-dark-border bg-dark-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
                            <div>
                                <h3 className="font-bold text-dark-text">İşlem Geçmişi</h3>
                                <p className="text-xs text-dark-muted mt-0.5">{auditShipment.seller} → {auditShipment.buyer} ({auditShipment.sackCount} çuval)</p>
                            </div>
                            <button onClick={() => setAuditShipment(null)} className="p-2 text-dark-muted hover:text-dark-text rounded-lg hover:bg-dark-hover transition-colors">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto px-6 py-4">
                            {auditLoading ? (
                                <div className="py-8 flex justify-center">
                                    <div className="w-6 h-6 rounded-full border-t-2 border-r-2 border-primary-500 animate-spin"></div>
                                </div>
                            ) : auditLogs.length > 0 ? (
                                <ul className="space-y-3">
                                    {auditLogs.map((log) => (
                                        <li key={log.id} className="flex items-start justify-between gap-3 text-sm">
                                            <div>
                                                <div className="font-medium text-dark-text">{AUDIT_ACTION_LABELS[log.actionType] || log.actionType}</div>
                                                {log.note && <div className="text-xs text-dark-muted mt-0.5">{log.note}</div>}
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-xs font-medium text-dark-text">{log.username}</div>
                                                <div className="text-[11px] text-dark-muted mt-0.5">{formatRecordDate(log.createdAt)} {formatRecordTime(log.createdAt)}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="py-8 text-center text-sm text-dark-muted">Bu kayıt için işlem geçmişi yok.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Shipments;
